import { PRESET_DESCRIPTION } from "../core/primitives/shop-page";
import type { ProfileDraft, ProfileErrors, ProfileField } from "../core/primitives/store-profile";
import { LogoField } from "../sections/logo-field/logo-field";

const onboardingLabelCls = "mb-1.5 block text-xs font-medium text-white";

/**
 * The shop profile fields, shared by the wizard's first step and the settings
 * form. The logo control is the `logo-field` section the page controller owns.
 * `variant` keeps each screen's own layout and copy.
 */
export function StoreProfileFields({
  variant,
  draft,
  errors,
  requireDescription,
  descriptionHint,
  onChange,
}: {
  variant: "onboarding" | "settings";
  draft: ProfileDraft;
  errors: ProfileErrors;
  /** A shop that hosts its own page can no longer clear its description. */
  requireDescription: boolean;
  /** The onboarding step's sentence under the description, when there is no error. */
  descriptionHint?: string;
  onChange: (field: ProfileField, value: string) => void;
}) {
  if (variant === "onboarding") {
    return (
      <>
        <div>
          <label htmlFor="name" className={onboardingLabelCls}>
            Shop name
          </label>
          <input
            id="name"
            value={draft.name}
            onChange={(e) => onChange("name", e.target.value)}
            className="field"
            placeholder="Card Corner"
          />
          {errors.name && <p className="field-error">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="description" className={onboardingLabelCls}>
            Description {!requireDescription && <span className="font-normal text-faint">Optional</span>}
          </label>
          <textarea
            id="description"
            value={draft.description}
            onChange={(e) => onChange("description", e.target.value)}
            rows={2}
            className="field resize-y"
            placeholder={PRESET_DESCRIPTION}
          />
          {errors.description ? (
            <p className="field-error">{errors.description}</p>
          ) : (
            <p className="mt-1.5 text-[11px] leading-relaxed text-faint">{descriptionHint}</p>
          )}
        </div>

        <div className="flex gap-3">
          <div className="flex-1">
            <LogoField labelClassName={onboardingLabelCls} />
            {errors.logoUrl && <p className="field-error">{errors.logoUrl}</p>}
          </div>
          <div className="w-[104px]">
            <label htmlFor="currencyCode" className={onboardingLabelCls}>
              Currency
            </label>
            <input
              id="currencyCode"
              value={draft.currencyCode}
              onChange={(e) => onChange("currencyCode", e.target.value)}
              maxLength={3}
              className="field uppercase"
              placeholder="USD"
            />
            {errors.currencyCode && <p className="field-error">{errors.currencyCode}</p>}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="name" className="label">
          Store name
        </label>
        <input
          id="name"
          value={draft.name}
          onChange={(e) => onChange("name", e.target.value)}
          className="field"
          placeholder="My card shop"
        />
        {errors.name && <p className="field-error">{errors.name}</p>}
      </div>

      <div>
        <label htmlFor="description" className="label">
          Description {!requireDescription && <span className="font-normal text-faint">(optional)</span>}
        </label>
        <textarea
          id="description"
          value={draft.description}
          onChange={(e) => onChange("description", e.target.value)}
          rows={3}
          className="field resize-y"
          placeholder="What does this store sell?"
        />
        {errors.description ? (
          <p className="field-error">{errors.description}</p>
        ) : (
          requireDescription && (
            <p className="mt-1 text-xs leading-relaxed text-faint">
              Shown on your shop page, and part of what Stripe checks when it verifies your business.
            </p>
          )
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <LogoField />
          {errors.logoUrl && <p className="field-error">{errors.logoUrl}</p>}
        </div>
        <div>
          <label htmlFor="currencyCode" className="label">
            Default currency
          </label>
          <input
            id="currencyCode"
            value={draft.currencyCode}
            onChange={(e) => onChange("currencyCode", e.target.value)}
            maxLength={3}
            className="field uppercase"
            placeholder="USD"
          />
          {errors.currencyCode && <p className="field-error">{errors.currencyCode}</p>}
        </div>
      </div>
    </div>
  );
}
